import axios from "../config/axios-rag"

const DEFAULT_CONFIG = {
    llmModel: "gpt-4o-mini",
    embeddingModel: "text-embedding-3-small",
    chunkSize: 1200,
    chunkOverlap: 100,
    maxTokens: 4096,
    temperature: 0.7,
    topK: 60,
    enableGraphSearch: true,
    graphCommunityLevel: 2,
};

function mapServer(s) {
    return {
        id: s.topicId,
        host: s.host,
        port: s.port,
        apiBase: s.apiBase,
        hasApiKey: s.hasApiKey,
        status: s.status,
        workingDir: s.workingDir ?? undefined,
        lastHealthAt: s.lastHealthAt ?? undefined,
    };
}

function mapConfig(c) {
    return {
        llmModel: c.llmModel,
        embeddingModel: c.embeddingModel,
        chunkSize: c.chunkSize,
        chunkOverlap: c.chunkOverlap,
        maxTokens: c.maxTokens,
        temperature: c.temperature,
        topK: c.topK,
        enableGraphSearch: c.enableGraphSearch,
        graphCommunityLevel: c.graphCommunityLevel,
    };
}

function mapTopic(t) {
    return {
        id: t.id,
        name: t.name,
        description: t.description,
        tags: t.tags,
        color: t.color,
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
        documentCount: t.documentCount,
        queryCount: t.queryCount,
        server: t.server ? mapServer(t.server) : null,
        config: t.config ? mapConfig(t.config) : DEFAULT_CONFIG,
        documents: [],
    };
}

function mapQuery(q) {
    return {
        id: q.id,
        query: q.query,
        response: q.response,
        mode: q.mode,
        sources: q.sources,
        timestamp: q.createdAt,
        topK: q.topK,
        tokensUsed: q.tokensUsed,
    };
}


function mapDocument(d) {
    return {
        id: d.id,
        name: d.name,
        type: d.mimeType,
        size: d.sizeBytes,
        status: d.status,
        uploadedAt: d.uploadedAt,
        chunks: d.chunks,
        errorMessage: d.errorMessage ?? undefined,
    };
}

function basePath(topicId) {
    return `/api/topics/${topicId}`;
}

function buildQuery(params) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === "") continue;
        search.set(key, String(value));
    }

    const query = search.toString();
    return query ? `?${query}` : "";
}

function normalizeNodeType(rawType) {
    const value = rawType?.toLowerCase();
    if (value === "relation") return "relation";
    if (value === "chunk") return "chunk";
    return "entity";
}

function mapNode(node, fallbackIndex) {
    const id = String(node.id ?? node.label ?? node.name ?? `node-${fallbackIndex}`);
    const label = String(node.label ?? node.name ?? node.id ?? `Node ${fallbackIndex + 1}`);
    const type = normalizeNodeType(node.type ?? node.entity_type ?? node.nodeType ?? node.kind);
    const properties = (node.properties ?? node.data ?? {});

    return {
        id,
        label,
        type,
        properties,
    };
}

function mapEdge(edge, fallbackIndex) {
    return {
        source: String(edge.source ?? edge.source_id ?? edge.from ?? ""),
        target: String(edge.target ?? edge.target_id ?? edge.to ?? ""),
        label: String(edge.label ?? edge.relation ?? edge.type ?? `edge-${fallbackIndex + 1}`),
        weight: edge.weight,
    };
}

function mapGraphResponse(raw) {
    const nodes = (raw.nodes ?? []).map(mapNode);
    const edges = (raw.edges ?? []).map(mapEdge);

    return {
        nodes,
        edges,
        totalEntities:
            typeof raw.totalEntities === "number" ? raw.totalEntities : raw.total_entities,
        totalRelationships:
            typeof raw.totalRelationships === "number"
                ? raw.totalRelationships
                : raw.total_relationships,
        raw,
    };
}

export const KnowledgeDatabaseRepository = {

    async deleteDocument(docId) {
        return axios.delete(`/api/documents/${docId}`);
    },
    async uploadDocument(topicId, file, onProgress) {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(`/api/topics/${topicId}/documents`, formData, {
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                    const pct = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    onProgress?.(pct);
                }
            },
        });

        return mapDocument(response.data);
    },
    async listDocuments(topicId) {
        const response = await axios.get(`/api/topics/${topicId}/documents`);
        return response.data.map(mapDocument);
    },
    async listLabels(topicId) {
        const response = await axios.get(`${basePath(topicId)}/graph/label/list`);
        return response.data;
    },
    async listPopularLabels(topicId, limit) {
        const response = await axios.get(
            `${basePath(topicId)}/graph/label/popular${buildQuery({limit})}`,
        );
        return response.data;
    },
    async searchLabels(topicId, q, limit) {
        const response = await axios.get(
            `${basePath(topicId)}/graph/label/search${buildQuery({q, limit})}`,
        );
        return response.data;
    },
    async fetch(topicId, options) {
        const raw = await axios.get(
            `${basePath(topicId)}/graph${buildQuery({
                label: options.label,
                maxDepth: options.maxDepth,
                maxNodes: options.maxNodes,
            })}`,
        );

        return mapGraphResponse(raw.data);
    },
    async entityExists(topicId, name) {
        const response = await axios.get(
            `${basePath(topicId)}/graph/entity/exists${buildQuery({name})}`,
        );
        return response.data;
    },
    async updateEntity(topicId, body) {
        const response = await axios.post(`${basePath(topicId)}/graph/entity/edit`, body);
        return response.data;
    },
    async updateRelation(topicId, body) {
        const response = await axios.post(`${basePath(topicId)}/graph/relation/edit`, body);
        return response.data;
    },

    async createEntity(topicId, body) {
        const response = await axios.post(`${basePath(topicId)}/graph/entity/create`, body);
        return response.data;
    },

    async createRelation(topicId, body) {
        const response = await axios.post(`${basePath(topicId)}/graph/relation/create`, body);
        return response.data;
    },

    async mergeEntities(topicId, body) {
        const response = await axios.post(`${basePath(topicId)}/graph/entities/merge`, body);
        return response.data;
    },
    async listTopics(organizationId) {
        try {
            const raw = await axios.get("/api/topics", {
                params: {organizationId}
            });
            return raw.data.map(mapTopic);
        } catch (err) {
            console.log(err.message)
        }
    },
    async getById(id) {
        const raw = await axios.get(`/api/topics/${id}`);
        return mapTopic(raw.data);
    },
    async create(req) {
        const raw = await axios.post("/api/topics", req);
        return mapTopic(raw.data);
    },
    async patch(id, req) {
        const raw = await axios.patch(`/api/topics/${id}`, req);
        return mapTopic(raw.data);
    },


    async deleteTopic(id) {
        return axios.delete(`/api/topics/${id}`);
    },

    async upsertServer(topicId, req) {
        const raw = await axios.put(`/api/topics/${topicId}/server`, req);
        return mapServer(raw.data);
    },

    async checkHealth(topicId) {
        const raw = await axios.post(`/api/topics/${topicId}/server/health`);
        const s = raw.data.status;
        return s === "online" || s === "offline" || s === "connecting" ? s : "offline";
    },

    async getConfig(topicId) {
        const raw = await axios.get(`/api/topics/${topicId}/config`);
        return mapConfig(raw.data);
    },

    async upsertConfig(topicId, req) {
        const raw = await axios.put(`/api/topics/${topicId}/config`, req);
        return mapConfig(raw.data);
    },
    async listQueries(topicId, limit = 50) {
        const raw = await axios.get(
            `/api/topics/${topicId}/queries?limit=${limit}`,
        );
        return raw.data.map(mapQuery);
    },
    async executeQuery(topicId, req) {
        const raw = await axios.post(
            `/api/topics/${topicId}/queries`,
            req,
        );
        return mapQuery(raw.data);
    },
}